import { formatPublicEventDate, publicEventTimezone } from "./publicSiteUtils";

export function PublicEventsSection({
  busyEventId = "",
  chapterOrganization = false,
  currentMember = null,
  eventPluralLabel = "events",
  events = [],
  formatEventCapacity,
  isAuthenticated = false,
  memberConsoleMode = false,
  onRegisterForEvent,
  onRequestQualification,
  publicEventAvailabilityLabel,
  publicEventDetailText,
  publicEventInstructorText,
  publicEventOrientationSummary,
  publicEventPriceLabel,
  publicEventPrimaryActionLabel,
  publicEventRegistrationStatusLabel,
  publicEventRegistrationWindowLabel,
  publicEventTitleText,
  publicEventTypeLabel,
  qualificationDisplayLabel,
  qualificationKeyList,
  registrationByEventId,
  requestingQualificationKey = "",
  range = {},
  summarizeEventAccess
}) {
  if (events.length === 0) {
    return null;
  }

  return (
    <div className="public-events-panel" id="events">
      <div>
        <h3>{memberConsoleMode ? "Classes & events" : (chapterOrganization ? "Upcoming classes" : "Upcoming events")}</h3>
        <p>{memberConsoleMode ? `Register for published ${eventPluralLabel} tied to this membership.` : (chapterOrganization ? "Published chapter classes and organization events." : "Published public classes and range events.")}</p>
      </div>
      <div className="public-events-list">
        {events.map((event) => {
          const existingRegistration = registrationByEventId.get(event.eventId);
          const activeRegistration = existingRegistration && !["cancelled"].includes(existingRegistration.status);
          const availabilityLabel = publicEventAvailabilityLabel(event);
          const eventTimezone = publicEventTimezone(event, range);
          const registrationWindowLabel = publicEventRegistrationWindowLabel(event, eventTimezone);
          const registrationAvailable = !availabilityLabel;
          const eventQualificationLabels = (event.accessPolicy?.requiredQualificationKeys || []).map(qualificationDisplayLabel);
          const eventAccess = summarizeEventAccess(event, currentMember);
          const eventAccessBlocked = isAuthenticated && eventAccess.requiredKeys.length > 0 && !eventAccess.allowed;
          const memberOnlyEvent = event.visibility === "members" || event.accessPolicy?.membershipRequired === true;
          const memberOnlyBlocked = memberOnlyEvent && (!currentMember || currentMember.status !== "active");
          const eventTypeLabel = publicEventTypeLabel(event);
          const orientationSummary = publicEventOrientationSummary(event);
          const eventDescription = publicEventDetailText(event.description, "Chapter class details will be confirmed by the local team.");

          return (
            <article key={event.eventId}>
              <div>
                <div className="public-event-title-row">
                  <strong>{publicEventTitleText(event.title)}</strong>
                  <span className={`public-event-type-badge ${orientationSummary ? "is-orientation" : ""}`}>{eventTypeLabel}</span>
                </div>
                {eventDescription && <span>{eventDescription}</span>}
                {orientationSummary && <span className="public-event-orientation-copy">{orientationSummary}</span>}
                <span>{formatPublicEventDate(event.startsAt, eventTimezone)}</span>
                <span>{event.location || "Location announced by the organization"}</span>
                {event.instructors?.length > 0 && (
                  <span>
                    Instructor: {event.instructors.map(publicEventInstructorText).join(", ")}
                  </span>
                )}
                {eventQualificationLabels.length > 0 && (
                  <span className="public-event-access-note">
                    <span>Qualification required: {eventAccess.label}</span>
                    {isAuthenticated && !currentMember && (
                      <small>Start or complete membership before requesting this access.</small>
                    )}
                    {isAuthenticated && currentMember && eventAccess.pendingLabels.length > 0 && (
                      <small>Review pending: {qualificationKeyList(eventAccess.pendingLabels)}</small>
                    )}
                    {isAuthenticated && currentMember && eventAccess.missingKeys.length > 0 && (
                      <span className="public-event-access-actions">
                        {eventAccess.missingKeys.map((key) => (
                          <button
                            type="button"
                            className="secondary-button"
                            key={key}
                            disabled={requestingQualificationKey === key}
                            onClick={() => onRequestQualification(key)}
                          >
                            {requestingQualificationKey === key ? "Requesting..." : `Request ${qualificationDisplayLabel(key)}`}
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                )}
                {memberOnlyEvent && (
                  <span className="public-event-access-note public-event-membership-note">
                    <span>Member-only registration</span>
                    {currentMember?.status === "active" ? (
                      <small>Your active membership can register for this program.</small>
                    ) : currentMember ? (
                      <small>Your membership must be active before registration opens for your account.</small>
                    ) : (
                      <small>Start or complete membership before registering for this program.</small>
                    )}
                  </span>
                )}
                <span>{formatEventCapacity(event)}</span>
                <span>{publicEventPriceLabel(event)}</span>
                {registrationWindowLabel && <span>{registrationWindowLabel}</span>}
                {availabilityLabel && <span>{availabilityLabel}</span>}
                {publicEventDetailText(event.registrationPolicy?.instructions) && (
                  <span>{publicEventDetailText(event.registrationPolicy.instructions)}</span>
                )}
                {activeRegistration && <span>Your status: {publicEventRegistrationStatusLabel(existingRegistration.status)}</span>}
              </div>
              {existingRegistration?.checkout?.url ? (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => window.location.assign(existingRegistration.checkout.url)}
                >
                  Continue checkout
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busyEventId === event.eventId || Boolean(activeRegistration) || !registrationAvailable || eventAccessBlocked || memberOnlyBlocked}
                  onClick={() => onRegisterForEvent(event)}
                >
                  {activeRegistration
                    ? "Registered"
                    : publicEventPrimaryActionLabel(event, {
                      activeRegistration,
                      availabilityLabel,
                      busy: busyEventId === event.eventId,
                      currentMember,
                      eventAccess,
                      eventAccessBlocked,
                      isAuthenticated,
                      memberOnlyBlocked
                    })}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
